import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Download, Trash2, Crown, Mail, Check, Sparkles, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { Toaster, toast } from "sonner";

const PLAN_ACCENT = {
    free: "from-aura-muted to-aura-dim",
    premium: "from-aura-violet to-aura-glow",
    pro: "from-aura-cyan to-aura-blue",
};

export default function Settings() {
    const { user, logout, refresh } = useAuth();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [checkingPayment, setCheckingPayment] = useState(false);

    // Load plans
    useEffect(() => {
        api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
    }, []);

    // Poll payment status if returning from Stripe
    useEffect(() => {
        const sessionId = params.get("session_id");
        if (!sessionId) return;
        setCheckingPayment(true);
        let attempts = 0;
        const max = 8;
        const poll = async () => {
            attempts++;
            try {
                const { data } = await api.get(`/stripe/status/${sessionId}`);
                if (data.paymentStatus === "paid") {
                    await refresh();
                    toast.success(`Upgrade successful — welcome to ${data.planId.toUpperCase()}!`);
                    setCheckingPayment(false);
                    setParams({}, { replace: true });
                    return;
                }
                if (data.status === "expired") {
                    toast.error("Payment session expired");
                    setCheckingPayment(false);
                    setParams({}, { replace: true });
                    return;
                }
                if (attempts < max) setTimeout(poll, 2000);
                else {
                    setCheckingPayment(false);
                    setParams({}, { replace: true });
                }
            } catch {
                setCheckingPayment(false);
            }
        };
        poll();
    }, [params, refresh, setParams]);

    useEffect(() => {
        if (params.get("canceled")) {
            toast.info("Payment canceled");
            setParams({}, { replace: true });
        }
    }, [params, setParams]);

    const upgrade = async (planId) => {
        setLoading(true);
        try {
            const { data } = await api.post("/stripe/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (err) {
            toast.error(err?.response?.data?.error?.message || "Checkout failed");
            setLoading(false);
        }
    };

    const exportData = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/user/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `MirrorDigital-aura-export-${Date.now()}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Export downloaded");
        } catch {
            toast.error("Export failed");
        } finally {
            setLoading(false);
        }
    };

    const deleteAccount = async () => {
        if (!window.confirm("Delete your account permanently? This cannot be undone.")) return;
        setLoading(true);
        try {
            await api.delete("/user/me");
            logout();
            navigate("/login");
            toast.success("Account deleted");
        } catch {
            toast.error("Delete failed");
            setLoading(false);
        }
    };

    return (
        <Layout>
            <Toaster theme="light" position="top-center" />
            <div className="mb-8">
                <div className="font-jetbrains-mono text-xs tracking-[0.3em] text-aura-violet mb-2">
                    // SETTINGS
                </div>
                <h1 className="font-syne text-4xl font-bold text-aura-text">Account</h1>
            </div>

            <div className="space-y-6 max-w-4xl">
                {/* Payment check banner */}
                {checkingPayment && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="glass p-5 flex items-center gap-4 border-aura-violet/30"
                    >
                        <div className="cyber-spinner !w-6 !h-6" />
                        <div>
                            <div className="font-syne text-base font-semibold text-aura-text">Confirming your payment…</div>
                            <div className="text-xs text-aura-body">This usually takes a few seconds.</div>
                        </div>
                    </motion.div>
                )}

                {/* Profile */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass p-6" data-testid="profile-card">
                    <h3 className="font-syne text-xl font-semibold mb-4 text-aura-text">Profile</h3>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <Mail className="w-4 h-4 text-aura-muted" />
                            <span className="font-dm-sans text-sm text-aura-text">{user?.email}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Crown className="w-4 h-4 text-aura-violet" />
                            <span className="font-jetbrains-mono text-xs uppercase tracking-wider text-aura-violet font-semibold">{user?.tier} tier</span>
                        </div>
                    </div>
                </motion.div>

                {/* Pricing / Upgrade */}
                <motion.div id="upgrade" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="glass p-6" data-testid="upgrade-card">
                    <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-aura-violet" />
                        <h3 className="font-syne text-xl font-semibold text-aura-text">Plans</h3>
                    </div>
                    <p className="text-xs text-aura-body mb-5">Upgrade anytime. Cancel anytime. Secured by Stripe.</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {plans.map((p) => {
                            const isCurrent = user?.tier === p.tier;
                            return (
                                <div
                                    key={p.id}
                                    className={`relative rounded-xl p-5 border transition ${
                                        isCurrent
                                            ? "border-aura-violet/40 bg-aura-violet/5"
                                            : "border-aura-violet/10 bg-white hover:border-aura-violet/30"
                                    }`}
                                    data-testid={`plan-card-${p.id}`}
                                >
                                    {isCurrent && (
                                        <div className="absolute -top-2 right-3 font-jetbrains-mono text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-aura-violet text-white">
                                            Current
                                        </div>
                                    )}
                                    <div className="font-syne text-lg font-bold text-aura-text">{p.name}</div>
                                    <div className="flex items-baseline gap-1 my-2">
                                        <span className={`font-syne text-3xl font-bold bg-gradient-to-r ${PLAN_ACCENT[p.id]} bg-clip-text text-transparent`}>
                                            {p.amount === 0 ? "Free" : `${p.amount}€`}
                                        </span>
                                        {p.amount > 0 && <span className="text-xs text-aura-muted">/mo</span>}
                                    </div>
                                    <ul className="space-y-1.5 mb-5">
                                        {p.features.map((f) => (
                                            <li key={f} className="flex items-start gap-2 text-xs text-aura-body">
                                                <Check className="w-3.5 h-3.5 text-aura-violet shrink-0 mt-0.5" strokeWidth={2.5} />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    {!isCurrent && p.id !== "free" && (
                                        <button
                                            onClick={() => upgrade(p.id)}
                                            disabled={loading}
                                            className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-aura-violet to-aura-glow text-white font-dm-sans text-sm font-medium glow-violet-soft btn-ripple disabled:opacity-60 flex items-center justify-center gap-2"
                                            data-testid={`upgrade-${p.id}-btn`}
                                        >
                                            {loading ? "…" : (<>Upgrade <ExternalLink className="w-3.5 h-3.5" /></>)}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </motion.div>

                {/* GDPR */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass p-6" data-testid="gdpr-card">
                    <h3 className="font-syne text-xl font-semibold mb-1 text-aura-text">Data & Privacy</h3>
                    <p className="text-xs text-aura-body mb-5">GDPR rights — export or delete your data anytime.</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <motion.button
                            whileTap={{ scale: 0.97 }}
                            onClick={exportData}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-aura-cyan/30 text-aura-cyan hover:bg-aura-cyan/10 transition btn-ripple disabled:opacity-60 font-medium"
                            data-testid="export-data-btn"
                        >
                            <Download className="w-4 h-4" /> Export my data
                        </motion.button>
                        <motion.button
                            whileTap={{ scale: 0.97 }}
                            onClick={deleteAccount}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-red-500/30 text-red-500 hover:bg-red-500/10 transition btn-ripple disabled:opacity-60 font-medium"
                            data-testid="delete-account-btn"
                        >
                            <Trash2 className="w-4 h-4" /> Delete account
                        </motion.button>
                    </div>
                </motion.div>
            </div>
        </Layout>
    );
}

